import {
	Box,
	Typography,
	Collapse,
	Fade,
	Tooltip,
} from "@mui/material"
import { useState } from "react"
import { selectNoteAttachmentBreadcrumbs } from "../../../../../reducers/noteAttachmentsSlice"
import {useDispatch, useSelector} from "react-redux"
import { clearState, selectTargetArticle, setTargetObject } from "../../../../../reducers/targetArticleSlice"
import { setDragItem } from "../../../../../reducers/articleDragSlice"
import { dragType } from "../../../../../reducers/articleDragSlice"
import DeleteAttachmentModal from "../NoteAttachmentModals/DeleteAttachmentModal"
import NoteAttachmentMenu from "./NoteAttachmentMenu"
import {AttachmentIcon} from "./AttachmentIcon"
import {AttachmentBody, DescriptionPreview} from "./AttachmentBody"
import DiscreteOpenButton from "../../../../../utils/DiscreteOpenButton"
import {useWorkspaceContext} from "../../../../../pages/Dashboard"

export default function Attachment({attachment, preview}) {
	const dispatch = useDispatch()
	const { workspaceId, compositeKey } = useWorkspaceContext()
	const [isHovered, setIsHovered] = useState(false)
	const [isDragHover, setIsDragHover] = useState(false)
	const [isDragging, setIsDragging] = useState(false)
	const [openDeleteModal, setOpenDeleteModal] = useState(false)
	const noteAttachmentBreadcrumbs = useSelector((state) => selectNoteAttachmentBreadcrumbs(state, workspaceId))

	const targetObjectId = useSelector((state) => selectTargetArticle(state, workspaceId))
	const open = "attachment/" + attachment.id === targetObjectId

	const handleClick = () => {
		if (open) {
			// reset target article state
			dispatch(clearState(workspaceId))
		} else {
			// set target article state
			dispatch(setTargetObject({attachment, workspaceId}))
		}
	}
	
	const handleDragStart = (event) => {
		setIsDragging(true)

		// dispatch anywhere in this drag handler causes a visual lag in firefox
		// need to request animation frame to perform dispatch to avoid visual lag
		requestAnimationFrame(() => {
			dispatch(setDragItem({
				item: attachment,
				type: dragType.ATTACHMENT,
				workspaceId,
				compositeKey,
			}))
		})

		event.dataTransfer.effectAllowed = "move"
		event.dataTransfer.setData("text/plain", `note-attachment-${attachment.id}`)
	
		// Create a new element to be used as the drag image
		const dragImage = document.getElementById("custom-drag-container")
		const dragText = document.getElementById("custom-drag-text")
		
		if (dragImage) {
			dragText.textContent = attachment.attachmentType === "doc" ? "Dragging 1 document" : "Dragging 1 attachment"
			event.dataTransfer.setDragImage(dragImage, 0, 0) // Center the drag image under the cursor
	
		} else {
			console.error("Drag UI Not Located")
		}

		event.currentTarget.addEventListener("dragend", () => {
			setIsDragging(false)
		})
	}

	return (
		<Box 
			onMouseEnter={() => setIsHovered(true)}
			onMouseLeave={() => setIsHovered(false)}
		>
			<Box
				variant="note"
				sx={{
					position: "relative",
					display: "flex",
					width: "100%",
					color: "RBOneFont.main",
					backgroundColor: "notes.background",
					boxShadow: "0 0px 4px rgba(0, 0, 0, 0.2)",
					alignItems: "center",
					borderRadius: "4px",
					overflow: "hidden"
				}}
			>
				<Fade
					in={open || preview}
					mountOnEnter
					unmountOnExit
				>
					<div> { /* Div wrapper to facilitate <Fade> component */ }
						<NoteAttachmentMenu
							setOpenDeleteModal={setOpenDeleteModal}
							preview={preview}
							attachment={attachment}
						/>
					</div>
				</Fade>
				<Tooltip
					title={isDragging ? "" : "Drag to attach to note"}
					followCursor
					placement={"left"}
					enterDelay={1000}
					sx={{ display: isDragging ? "none" : "initial" }} // this doesn't work after replacing DnD Kit drag values with  local state
				>
					<div
						draggable="true"
						onDragStart={handleDragStart}
						onMouseEnter={() => setIsDragHover(true)}
						onMouseLeave={() => setIsDragHover(false)}
						onClick={() => null}
						style={{
							minWidth: "32px",
							width: "32px",
							alignSelf: "stretch", 
							backgroundColor: (isDragging && !preview) ? "#B9E7EC" : ( isDragHover ?  "#1095A2" : "#02B3C5"),
							cursor: "pointer"
						}}>
							&nbsp;
					</div>
				</Tooltip>

				<div id="ICON_CONTAINER_DIV" style={{
					display: "flex",
					padding: "8px",
					overflow: "hidden",
					gap: "8px",
					alignItems: "flex-start",
					width: "100%"
				}}>
					<AttachmentIcon
						attachment={attachment}
						handleClick={handleClick}
						isTargetObject={open}
					/>
					<Box sx={{
						display: (open || preview) ? "box" : "flex",
						alignItems: "center", gap: 1,
						width: "100%",
						paddingRight: "8px",
						overflow: "hidden"}}
					>
						<Typography
							variant={"body1"}
							sx={{
								marginBottom: 0,
								wordBreak: "break-word",
								fontWeight: "bold",
							}}
							paragraph
						>
							{attachment.title}
						</Typography>
						{ !open && !preview && (
							<>
								<DescriptionPreview attachment={attachment} />
								<Fade in={isHovered}>
									<div style={{
										right: "4px",
										paddingLeft: "25px",
										background: "linear-gradient(to right, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 1) 10%)"
									}}>
										<DiscreteOpenButton
											onClick={handleClick}
											variant={"text"}
											color={"otherInfo"}
											sx={{
												textTransform: "none",
												padding: "4px 16px",
												marginBottom: "4px",
												"&:hover": { backgroundColor: "primary.transparency20", }
											}}
										>
											Details
										</DiscreteOpenButton>
									</div>
								</Fade>
							</>
						) }
						<Collapse in={open || preview} timeout="auto" mountOnEnter unmountOnExit>
							{													
								(open || preview) && (
									<AttachmentBody
										attachment={attachment}
										noteAttachmentBreadcrumbs={noteAttachmentBreadcrumbs}
										isPreview={preview || open}
									/>
								)
							}

						</Collapse>
					</Box>
				</div>
				<DeleteAttachmentModal
					attachment={attachment}
					openDeleteModal={openDeleteModal}
					setOpenDeleteModal={setOpenDeleteModal}

				/>
			</Box>
		</Box>
	)
}